import React from "react";

import { useAuthenticator } from "@aws-amplify/ui-react";
import type { AuthStatus } from "@aws-amplify/ui/dist/types/helpers/authenticator/facade";
import { Navigate } from "react-router-dom";

import type { ParticipantStatus, ParticipantTypes, UserStatus } from "@api/graphql";
import { type AuthenticationContext, useAuthenticationContext } from "@context/authentication";
import type { Role } from "@roles";

import { ROUTES } from "../../routes";

interface ProtectedComponentProps {
  children?: React.ReactNode;
  // NOTE: This requires at least one of the listed groups, not all.
  requiredGroups?: Role[];
  requiredUserStatus?: UserStatus;
  requiredParticipantStatus?: ParticipantStatus;
  requiredParticipantTypes?: ParticipantTypes[];
  redirectOnUnauth?: boolean;
  auth?: AuthenticationContext;
}

interface AuthorisationOptions {
  requiredGroups?: Role[];
  requiredUserStatus?: UserStatus;
  requiredParticipantStatus?: ParticipantStatus;
  requiredParticipantTypes?: ParticipantTypes[];
}

export const isAuthUserAllowed = (requiredGroups: Role[], groups?: string[]) => {
  if (groups) {
    const foundGroup = groups.find((group) => (requiredGroups as string[]).includes(group));
    return !!foundGroup;
  } else {
    // Skip userGroup check if auth hasn't been populated yet
    return true;
  }
};
export const useIsAuthUserAllowed = (requiredGroups: Role[]) => {
  const { auth } = useAuthenticationContext();
  const userGroups = auth?.userGroups || [];
  return isAuthUserAllowed(requiredGroups, userGroups);
};

export const isUserAndParticipantStatusValid = (
  auth: AuthenticationContext | undefined,
  requiredUserStatus?: UserStatus,
  requiredParticipantStatus?: ParticipantStatus,
) => {
  const isUserStatusValid = requiredUserStatus ? auth?.currentUserData?.user_status === requiredUserStatus : true;
  const isParticipantStatusValid = requiredParticipantStatus
    ? auth?.currentUserData?.participant?.participant_status === requiredParticipantStatus
    : true;
  return isUserStatusValid && isParticipantStatusValid;
};

export const isParticipantTypeAllowed = (
  auth: AuthenticationContext | undefined,
  requiredParticipantTypes?: ParticipantTypes[],
) => {
  if (!requiredParticipantTypes) {
    return true;
  }
  const userParticipantTypes = auth?.currentUserData?.participant?.participant_types;
  if (!userParticipantTypes || userParticipantTypes.length === 0) {
    return false;
  }
  return requiredParticipantTypes.some((type) => userParticipantTypes.includes(type));
};
export const useIsParticipantTypeAllowed = (requiredParticipantTypes: ParticipantTypes[]) => {
  const { auth } = useAuthenticationContext();
  return isParticipantTypeAllowed(auth, requiredParticipantTypes);
};

export const isAuthorisedUser = (auth: AuthenticationContext | undefined, options: AuthorisationOptions = {}) => {
  const { requiredGroups, requiredUserStatus, requiredParticipantStatus, requiredParticipantTypes } = options;

  return Boolean(
    auth &&
      (!requiredGroups || isAuthUserAllowed(requiredGroups, auth.userGroups)) &&
      isUserAndParticipantStatusValid(auth, requiredUserStatus, requiredParticipantStatus) &&
      isParticipantTypeAllowed(auth, requiredParticipantTypes),
  );
};
const getAuthenticatedElement = (
  auth: AuthenticationContext | undefined,
  isAuthorised: boolean | undefined,
  children: React.ReactNode,
  rootNav: JSX.Element,
) => {
  if (!auth) {
    return <></>;
  }

  if (auth?.currentUserData?.participant?.participant_status === "INCOMPLETE") {
    return <Navigate to={ROUTES.ExternalSignIn.path} />;
  }

  if (isAuthorised) {
    return <>{children}</>;
  }
  return rootNav;
};

export const useIsAuthorisedUser = (options: AuthorisationOptions = {}) => {
  const { auth } = useAuthenticationContext();
  return isAuthorisedUser(auth, options);
};

export const ProtectedComponent = ({
  children,
  requiredGroups,
  requiredUserStatus,
  requiredParticipantStatus,
  requiredParticipantTypes,
  redirectOnUnauth,
  auth,
}: ProtectedComponentProps): JSX.Element => {
  const { authStatus } = useAuthenticator((context) => [context.user]);
  const rootNav = redirectOnUnauth ? <Navigate to={ROUTES.Root.path} /> : <></>;
  const isAuthorised = isAuthorisedUser(auth, {
    requiredGroups,
    requiredUserStatus,
    requiredParticipantStatus,
    requiredParticipantTypes,
  });
  const elementMap: Record<AuthStatus, JSX.Element> = {
    configuring: <></>,
    unauthenticated: rootNav,
    authenticated: getAuthenticatedElement(auth, isAuthorised, children, rootNav),
  };

  return elementMap[authStatus];
};
